import React, { useState } from 'react';
import Card from '../components/ProfileCard.tsx';
import Button from '../components/Button';
import { User, Mail, Lock, Calendar, UserCircle } from 'lucide-react';

const Profile: React.FC = () => {
    const [profile, setProfile] = useState({
        name: 'John Doe',
        email: 'john.doe@example.com',
        password: '********',
        bio: 'Senior Web Developer with 5+ years of experience in creating responsive web applications.',
        role: 'Admin',
        createdAt: '2024-01-15',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    });

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ ...profile });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setProfile({ ...formData });
        setIsEditing(false);
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Manage your account information and settings
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-1">
                    <Card>
                        <div className="flex flex-col items-center">
                            <div className="relative">
                                <img
                                    src={profile.avatar}
                                    alt="Profile"
                                    className="w-32 h-32 rounded-full object-cover"
                                />
                                {isEditing && (
                                    <div className="absolute bottom-0 right-0">
                                        <label
                                            htmlFor="avatar-upload"
                                            className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white cursor-pointer"
                                        >
                                            <span className="sr-only">Upload avatar</span>
                                            <UserCircle size={18} />
                                        </label>
                                        <input
                                            id="avatar-upload"
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                        />
                                    </div>
                                )}
                            </div>
                            <h2 className="mt-4 text-xl font-semibold text-gray-900">
                                {profile.name}
                            </h2>
                            <p className="text-sm text-gray-500">{profile.email}</p>
                            <div className="mt-2 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                {profile.role}
                            </div>
                            <p className="mt-4 text-sm text-gray-500 flex items-center">
                                <Calendar size={16} className="mr-1" />
                                Member since {new Date(profile.createdAt).toLocaleDateString()}
                            </p>
                        </div>
                    </Card>
                </div>

                <div className="lg:col-span-2">
                    <Card>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-medium text-gray-900">
                                Account Information
                            </h3>
                            {!isEditing ? (
                                <Button
                                    variant="primary"
                                    onClick={() => setIsEditing(true)}
                                >
                                    Edit Profile
                                </Button>
                            ) : (
                                <div className="flex space-x-2">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setIsEditing(false);
                                            setFormData({ ...profile });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSubmit}
                                    >
                                        Save Changes
                                    </Button>
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="space-y-4">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Full Name
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User size={16} className="text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            name="name"
                                            id="name"
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            value={isEditing ? formData.name : profile.name}
                                            onChange={handleInputChange}
                                            readOnly={!isEditing}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label
                                        htmlFor="email"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Email Address
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail size={16} className="text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            id="email"
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            value={isEditing ? formData.email : profile.email}
                                            onChange={handleInputChange}
                                            readOnly={!isEditing}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label
                                        htmlFor="password"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Password
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Lock size={16} className="text-gray-400" />
                                        </div>
                                        <input
                                            type="password"
                                            name="password"
                                            id="password"
                                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                            value={isEditing ? formData.password : profile.password}
                                            onChange={handleInputChange}
                                            readOnly={!isEditing}
                                        />
                                    </div>
                                    {isEditing && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Leave blank to keep your current password
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label
                                        htmlFor="bio"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Bio
                                    </label>
                                    <div className="mt-1">
                    <textarea
                        id="bio"
                        name="bio"
                        rows={3}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        value={isEditing ? formData.bio : profile.bio}
                        onChange={handleInputChange}
                        readOnly={!isEditing}
                    />
                                    </div>
                                </div>

                                <div>
                                    <label
                                        htmlFor="role"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Role
                                    </label>
                                    <div className="mt-1">
                                        <select
                                            id="role"
                                            name="role"
                                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            value={isEditing ? formData.role : profile.role}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                        >
                                            <option value="Admin">Admin</option>
                                            <option value="Coach">Coach</option>
                                            <option value="Student">Student</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Profile;